import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from './AuthContext'
import { permissionsApi } from '../services/api/permissions'
import { resolvePermissions, isPlatformAdmin as checkPlatformAdmin } from '../lib/permissions'
import type { AccountUser, ExchangeUserPermission } from '../types/exchange'
import type { OrgMembership } from '../services/api/permissions'
import type { ResolvedPermissions } from '../lib/permissions'

export type OrgRole = OrgMembership['role'] | 'platform_admin'

interface PermissionContextType {
  accountUsers: AccountUser[]
  getExchangePermission: (exchangeId: string) => ExchangeUserPermission | null
  resolveForExchange: (exchangeId: string) => ResolvedPermissions
  isPlatformAdmin: boolean
  orgRoleForId: (orgId: string) => OrgRole | null
  isLoading: boolean
  refreshPermissions: () => Promise<void>
}

const PermissionContext = createContext<PermissionContextType | null>(null)

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth()
  const [accountUsers, setAccountUsers] = useState<AccountUser[]>([])
  const [exchangePermissions, setExchangePermissions] = useState<Map<string, ExchangeUserPermission | null>>(new Map())
  const [orgMemberships, setOrgMemberships] = useState<OrgMembership[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const platformAdmin = useMemo(() => {
    return user ? checkPlatformAdmin(user) : false
  }, [user])

  const loadPermissions = useCallback(async () => {
    if (!user) {
      setAccountUsers([])
      setExchangePermissions(new Map())
      setOrgMemberships([])
      return
    }

    setIsLoading(true)
    try {
      const result = await permissionsApi.getPermissions()

      setAccountUsers(Array.isArray(result.accountUsers) ? result.accountUsers : [])
      setOrgMemberships(Array.isArray(result.orgMemberships) ? result.orgMemberships : [])

      const permMap = new Map<string, ExchangeUserPermission | null>()
      for (const ep of (Array.isArray(result.exchangePermissions) ? result.exchangePermissions : [])) {
        permMap.set(ep.exchangeId, ep)
      }
      setExchangePermissions(permMap)
    } catch (error) {
      console.error('Failed to load permissions:', error)
    } finally {
      setIsLoading(false)
    }
  }, [user])

  useEffect(() => {
    loadPermissions()
  }, [loadPermissions])

  const getExchangePermission = useCallback(
    (exchangeId: string): ExchangeUserPermission | null => {
      return exchangePermissions.get(exchangeId) ?? null
    },
    [exchangePermissions],
  )

  const orgRoleForId = useCallback(
    (orgId: string): OrgRole | null => {
      if (platformAdmin) return 'platform_admin'
      const membership = orgMemberships.find(
        (m) => m.orgId === orgId && m.active
      )
      return membership?.role ?? null
    },
    [orgMemberships, platformAdmin],
  )

  const resolveForExchange = useCallback(
    (exchangeId: string): ResolvedPermissions => {
      if (!user) {
        return {
          accountRole: null,
          exchangeUserType: null,
          permissionLevel: 'read_only',
          features: {},
          canRead: false,
          canWrite: false,
          isPlatformAdmin: false,
          hasExchangeAccess: false,
        }
      }

      const accountUser = accountUsers.length > 0 ? accountUsers[0] : null
      const exchangePerm = exchangePermissions.get(exchangeId) ?? null

      return resolvePermissions(user, accountUser, exchangePerm)
    },
    [user, accountUsers, exchangePermissions],
  )

  const value = useMemo(
    () => ({
      accountUsers,
      getExchangePermission,
      resolveForExchange,
      isPlatformAdmin: platformAdmin,
      orgRoleForId,
      isLoading,
      refreshPermissions: loadPermissions,
    }),
    [accountUsers, getExchangePermission, resolveForExchange, platformAdmin, orgRoleForId, isLoading, loadPermissions],
  )

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  )
}

export function usePermissionContext(): PermissionContextType {
  const context = useContext(PermissionContext)
  if (!context) {
    throw new Error('usePermissionContext must be used within a PermissionProvider')
  }
  return context
}
